import { useState, useCallback } from 'react';
import { useAuthenticatedFetch } from '../../../../hooks/useAuthenticatedFetch';
import { ApiEndpoint, ApiResponse, ApiRequest, ApiDebugState } from '../../../../types/admin';
import { API_URL } from '../../../../utils/config';

export const useApiDebug = () => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [state, setState] = useState<ApiDebugState>({
    selectedEndpoint: null,
    response: null,
    request: null,
    loading: false,
    error: null,
    queryParams: {},
    pathParams: {},
    requestBody: '',
  });

  const executeEndpoint = useCallback(async (endpoint: ApiEndpoint, queryParams: Record<string, string>, pathParams: Record<string, string>, requestBody: string) => {
    setState(prev => ({
      ...prev,
      loading: true,
      error: null,
      response: null,
      request: null,
    }));

    const startTime = Date.now();
    
    // Build URL with path parameters
    let processedPath = endpoint.path;
    if (endpoint.pathParams) {
      endpoint.pathParams.forEach(param => {
        const value = pathParams[param.name];
        if (value && value.trim() !== '') {
          processedPath = processedPath.replace(`{${param.name}}`, encodeURIComponent(value.trim()));
        }
      });
    }

    // Build query string
    const queryString = new URLSearchParams();
    if (endpoint.queryParams) {
      endpoint.queryParams.forEach(param => {
        const value = queryParams[param.name];
        if (value && value.trim() !== '') {
          queryString.append(param.name, value.trim());
        }
      });
    }

    // Construct full URL
    const fullUrl = `${API_URL}${processedPath}${queryString.toString() ? '?' + queryString.toString() : ''}`;
    console.log('Debug: Executing API call to:', fullUrl);
    console.log('Debug: Query params:', queryParams);
    console.log('Debug: Path params:', pathParams);
    console.log('Debug: Request body:', requestBody);

    try {
      const fetchOptions: any = {
        method: endpoint.method,
      };

      // Prepare request body for non-GET requests
      let finalRequestBody = '';
      if (endpoint.method !== 'GET' && requestBody && requestBody.trim() !== '') {
        try {
          // Validate JSON if provided
          JSON.parse(requestBody);
          fetchOptions.body = requestBody.trim();
          finalRequestBody = requestBody.trim();
        } catch (jsonError) {
          setState(prev => ({
            ...prev,
            error: 'Invalid JSON in request body',
            loading: false,
          }));
          return;
        }
      }

      // Create request object for logging
      const apiRequest: ApiRequest = {
        method: endpoint.method,
        url: fullUrl,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer [TOKEN]', // Don't expose the actual token
        },
        body: finalRequestBody || undefined,
        timestamp: new Date().toISOString(),
      };

      // Store request information
      setState(prev => ({
        ...prev,
        request: apiRequest,
      }));

      const response = await authenticatedFetch(fullUrl, fetchOptions);

      const endTime = Date.now();

      if (!response) {
        setState(prev => ({
          ...prev,
          error: 'Authentication failed or no response received',
          loading: false,
        }));
        return;
      }

      console.log('Debug: Response status:', response.status);
      console.log('Debug: Response headers:', Object.fromEntries(response.headers.entries()));

      // Get response text first to handle both JSON and non-JSON responses
      const responseText = await response.text();
      console.log('Debug: Raw response text:', responseText);

      let responseData: any;
      try {
        // Try to parse as JSON
        responseData = JSON.parse(responseText);
      } catch (jsonError) {
        // If not JSON, use the text as-is
        console.log('Debug: Response is not JSON, using raw text');
        responseData = {
          error: 'Non-JSON response received',
          raw_response: responseText,
          content_type: response.headers.get('content-type') || 'unknown'
        };
      }

      const apiResponse: ApiResponse = {
        status: response.status,
        statusText: response.statusText,
        data: responseData,
        headers: Object.fromEntries(response.headers.entries()),
        timestamp: new Date().toISOString(),
        executionTime: endTime - startTime,
      };

      setState(prev => ({
        ...prev,
        response: apiResponse,
        loading: false,
      }));
    } catch (error) {
      console.error('Debug: API call error:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        loading: false,
      }));
    }
  }, [authenticatedFetch]);

  const clearResponse = useCallback(() => {
    setState(prev => ({
      ...prev,
      response: null,
      request: null,
      error: null,
      queryParams: {},
      pathParams: {},
      requestBody: prev.selectedEndpoint?.requestBodyTemplate || '',
    }));
  }, []);

  const setSelectedEndpoint = useCallback((endpoint: ApiEndpoint | null) => {
    setState(prev => ({
      ...prev,
      selectedEndpoint: endpoint,
      response: null,
      request: null,
      error: null,
      queryParams: {},
      pathParams: {},
      requestBody: endpoint?.requestBodyTemplate || '',
    }));
  }, []);

  const updateQueryParam = useCallback((name: string, value: string) => {
    setState(prev => ({
      ...prev,
      queryParams: {
        ...prev.queryParams,
        [name]: value,
      },
    }));
  }, []);

  const updatePathParam = useCallback((name: string, value: string) => {
    setState(prev => ({
      ...prev,
      pathParams: {
        ...prev.pathParams,
        [name]: value,
      },
    }));
  }, []);

  const updateRequestBody = useCallback((body: string) => {
    setState(prev => ({
      ...prev,
      requestBody: body,
    }));
  }, []);

  return {
    ...state,
    executeEndpoint,
    clearResponse,
    setSelectedEndpoint,
    updateQueryParam,
    updatePathParam,
    updateRequestBody,
  };
};